import React, { useState } from "react";
import { Copy, KeyRound, Webhook, Zap, ShieldCheck, Code2, ListChecks, AlertTriangle, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";

/* Public API documentation — optivize.digital/developers */

const BASE = "https://optivize.digital/api";

function CodeBlock({ title, code, lang = "bash" }) {
  return (
    <div className="rounded-xl overflow-hidden border border-slate-700 my-3">
      <div className="flex items-center justify-between bg-slate-800 px-3 py-1.5">
        <span className="text-[11px] font-mono text-slate-400">{title || lang}</span>
        <button onClick={async ()=>{ try{ await navigator.clipboard.writeText(code); toast.success("Copied"); }catch{ toast.error("Copy failed"); } }}
          className="text-slate-400 hover:text-white inline-flex items-center gap-1 text-[11px]" data-testid={`dev-copy-${(title||lang).toLowerCase().replace(/[^a-z0-9]+/g,"-")}`}>
          <Copy size={11}/> Copy
        </button>
      </div>
      <pre className="bg-slate-900 text-slate-200 text-[12px] leading-relaxed p-4 overflow-x-auto"><code>{code}</code></pre>
    </div>
  );
}

const H2 = ({ icon: Icon, children, id }) => (
  <h2 id={id} className="text-xl font-bold text-white mt-12 mb-3 flex items-center gap-2 scroll-mt-24" style={{fontFamily:"Outfit"}}>
    <Icon size={18} className="text-cyan-400"/>{children}
  </h2>
);

const P = ({ children }) => <p className="text-sm text-slate-400 leading-relaxed">{children}</p>;

function FieldsTable({ rows }) {
  return (
    <div className="overflow-x-auto my-3 rounded-xl border border-slate-700">
      <table className="w-full text-xs">
        <thead className="bg-slate-800 text-slate-400 uppercase tracking-wider">
          <tr><th className="text-left px-3 py-2">Field</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Required</th><th className="text-left px-3 py-2">Description</th></tr>
        </thead>
        <tbody>
          {rows.map(([f,t,r,d]) => (
            <tr key={f} className="border-t border-slate-800">
              <td className="px-3 py-2 font-mono text-cyan-300">{f}</td>
              <td className="px-3 py-2 text-slate-400">{t}</td>
              <td className="px-3 py-2 text-slate-400">{r}</td>
              <td className="px-3 py-2 text-slate-300">{d}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Developers() {
  const [tab, setTab] = useState("curl");
  const createSamples = {
    curl: `curl -X POST ${BASE}/v1/payments \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: ok_live_YOUR_KEY" \\
  -H "X-Api-Secret: sk_live_YOUR_SECRET" \\
  -d '{
    "amount": 4999,
    "currency": "INR",
    "gateway": "razorpay",
    "product_name": "Premium Website Package",
    "customer_first_name": "Rahul",
    "customer_last_name": "Sharma",
    "customer_email": "customer@example.com",
    "customer_phone": "+919876543210",
    "customer_address": "221B MG Road",
    "customer_city": "Bengaluru",
    "customer_state": "Karnataka",
    "customer_pin_code": "560001",
    "customer_country": "IN",
    "auto_checkout": true,
    "reference_id": "ORDER-12345",
    "metadata": {"campaign": "summer"},
    "webhook_url": "https://yoursite.com/webhooks/optivize"
  }'`,
    javascript: `const res = await fetch("${BASE}/v1/payments", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "X-Api-Key": process.env.OPTIVIZE_KEY,
    "X-Api-Secret": process.env.OPTIVIZE_SECRET,
  },
  body: JSON.stringify({
    amount: 4999,
    currency: "INR",
    product_name: "Premium Website Package",
    customer_first_name: "Rahul",
    customer_last_name: "Sharma",
    customer_email: "customer@example.com",
    customer_phone: "+919876543210",
    customer_address: "221B MG Road",
    customer_city: "Bengaluru",
    customer_state: "Karnataka",
    customer_pin_code: "560001",
    customer_country: "IN",
    auto_checkout: true,
    reference_id: "ORDER-12345",
    webhook_url: "https://yoursite.com/webhooks/optivize",
  }),
});
const payment = await res.json();
// Redirect your customer to the hosted checkout:
window.location.href = payment.payment_url;`,
    python: `import os, requests

r = requests.post("${BASE}/v1/payments", json={
    "amount": 4999,
    "currency": "INR",
    "product_name": "Premium Website Package",
    "customer_first_name": "Rahul",
    "customer_last_name": "Sharma",
    "customer_email": "customer@example.com",
    "customer_phone": "+919876543210",
    "customer_address": "221B MG Road",
    "customer_city": "Bengaluru",
    "customer_state": "Karnataka",
    "customer_pin_code": "560001",
    "customer_country": "IN",
    "auto_checkout": True,
    "reference_id": "ORDER-12345",
    "webhook_url": "https://yoursite.com/webhooks/optivize",
}, headers={
    "X-Api-Key": os.environ["OPTIVIZE_KEY"],
    "X-Api-Secret": os.environ["OPTIVIZE_SECRET"],
})
payment = r.json()
print(payment["payment_url"])   # send the customer here`,
    php: `<?php
$ch = curl_init("${BASE}/v1/payments");
curl_setopt_array($ch, [
  CURLOPT_POST => true,
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_HTTPHEADER => [
    "Content-Type: application/json",
    "X-Api-Key: " . getenv("OPTIVIZE_KEY"),
    "X-Api-Secret: " . getenv("OPTIVIZE_SECRET"),
  ],
  CURLOPT_POSTFIELDS => json_encode([
    "amount" => 4999,
    "currency" => "INR",
    "product_name" => "Premium Website Package",
    "customer_first_name" => "Rahul",
    "customer_last_name" => "Sharma",
    "customer_email" => "customer@example.com",
    "customer_phone" => "+919876543210",
    "customer_address" => "221B MG Road",
    "customer_city" => "Bengaluru",
    "customer_state" => "Karnataka",
    "customer_pin_code" => "560001",
    "customer_country" => "IN",
    "auto_checkout" => true,
    "reference_id" => "ORDER-12345",
    "webhook_url" => "https://yoursite.com/webhooks/optivize",
  ]),
]);
$payment = json_decode(curl_exec($ch), true);
header("Location: " . $payment["payment_url"]);`,
  };

  return (
    <div className="section max-w-4xl" data-testid="developers-page">
      <div className="tag mb-3 inline-flex items-center gap-1.5"><Code2 size={12}/> Developer API · v1</div>
      <h1 className="text-4xl sm:text-5xl font-extrabold text-white" style={{fontFamily:"Outfit"}}>
        Optivize <span className="text-cyan-300">Payments API</span>
      </h1>
      <p className="text-slate-400 mt-3 max-w-2xl">
        Collect payments on your own website through Optivize&apos;s <b className="text-white">Razorpay</b> and <b className="text-white">PayGlocal</b> gateways.
        Create a payment via the API, redirect your customer to the secure hosted checkout, and receive a signed webhook when it completes.
        No card data ever touches your servers — fully PCI-compliant.
      </p>

      <div className="mt-6 grid sm:grid-cols-3 gap-3 text-sm">
        {[
          [ShieldCheck, "Hosted checkout", "Branded, secure payment page supporting cards, UPI & international payments."],
          [Webhook, "Signed webhooks", "HMAC-SHA256 signed events on success, failure and cancellation."],
          [KeyRound, "API keys", "Key + secret pairs issued and managed by the Optivize admin."],
        ].map(([Icon, t, d]) => (
          <div key={t} className="card p-4">
            <Icon size={16} className="text-cyan-400"/>
            <div className="text-white font-semibold mt-2">{t}</div>
            <div className="text-slate-500 text-xs mt-1">{d}</div>
          </div>
        ))}
      </div>

      <H2 icon={KeyRound} id="auth">Authentication</H2>
      <P>Every request must include your API key pair as HTTP headers. Keys are issued from the Optivize admin console
        (Admin → Developer API). The secret is shown only once at creation — store it securely and never expose it in
        client-side/browser code.</P>
      <CodeBlock title="Headers" code={`X-Api-Key: ok_live_xxxxxxxxxxxxxxxxxxxxxxxx\nX-Api-Secret: sk_live_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx`}/>
      <P>Base URL: <code className="text-cyan-300 font-mono">{BASE}</code> · Test connectivity with <code className="text-cyan-300 font-mono">GET /v1/ping</code>.</P>

      <H2 icon={Zap} id="create">Create a payment</H2>
      <P><code className="text-emerald-400 font-mono">POST /v1/payments</code> — creates a payment and returns a checkout URL. Redirect your customer to <code className="text-cyan-300 font-mono">payment_url</code> — with <code className="text-cyan-300 font-mono">auto_checkout: true</code> it is the gateway's own hosted checkout page; otherwise it is the Optivize-hosted checkout form.</P>
      <FieldsTable rows={[
        ["amount", "number", "yes", "Payment amount entered by you (e.g. 4999 or 4999.50) — major units, not paise"],
        ["currency", "string", "no", "INR (default), USD, GBP, EUR, CAD, AUD, SGD"],
        ["gateway", "string", "no", "razorpay | payglocal — defaults to your key's first allowed gateway"],
        ["product_name", "string", "yes", "Shown to the customer on the checkout page & invoice"],
        ["reference_id", "string", "no", "Your internal order id — you can fetch the payment by it later"],
        ["customer_first_name", "string", "no", "Customer first name — prefilled on the hosted checkout"],
        ["customer_last_name", "string", "no", "Customer last name — prefilled on the hosted checkout"],
        ["customer_email", "string", "no", "Customer email address — prefilled on the hosted checkout"],
        ["customer_phone", "string", "no", "Customer phone number (with country code, e.g. +919876543210)"],
        ["customer_address", "string", "no", "Street address — prefilled on the hosted checkout"],
        ["customer_city", "string", "no", "City"],
        ["customer_state", "string", "no", "State / province"],
        ["customer_pin_code", "string", "no", "PIN / ZIP / postal code"],
        ["customer_country", "string", "no", "2-letter ISO country code (IN, US, GB, AE, SG, …)"],
        ["customer_name", "string", "no", "Legacy combined name — use first/last name instead"],
        ["auto_checkout", "boolean", "no", "true → payment_url in the response points DIRECTLY to the payment gateway's own hosted checkout page (Razorpay payment page / PayGlocal secure page) — the Optivize /pl page is skipped entirely. Requires first name, email, phone, address, city & state (else 400)"],
        ["metadata", "object", "no", "Arbitrary key-value data (max 4KB), echoed back in webhooks"],
        ["webhook_url", "string", "no", "Per-payment webhook override (defaults to the key's webhook URL)"],
        ["redirect_url", "string", "no", "Your website URL — after payment the customer is sent back here with signed status params (see Return redirect below)"],
        ["notes", "string", "no", "Internal note visible in the Optivize admin"],
      ]}/>
      <div className="flex gap-1.5 mt-4">
        {Object.keys(createSamples).map(l => (
          <button key={l} onClick={()=>setTab(l)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${tab===l ? "bg-cyan-600 border-cyan-500 text-white" : "bg-slate-800 border-slate-700 text-slate-400"}`}
            data-testid={`dev-lang-${l}`}>{l === "javascript" ? "JavaScript" : l === "php" ? "PHP" : l[0].toUpperCase()+l.slice(1)}</button>
        ))}
      </div>
      <CodeBlock title={`Create payment — ${tab}`} code={createSamples[tab]}/>
      <div className="rounded-xl border border-cyan-800/60 bg-cyan-500/5 p-4 my-3 text-sm">
        <div className="text-white font-semibold mb-1 flex items-center gap-2"><Zap size={14} className="text-cyan-400"/> Auto checkout (express flow)</div>
        <div className="text-slate-400 text-xs leading-relaxed">
          Pass <code className="text-cyan-300 font-mono">"auto_checkout": true</code> together with the full customer details
          (first name, email, phone, address, city, state) and the returned <code className="text-cyan-300 font-mono">payment_url</code>
          {" "}is the <b className="text-slate-200">payment gateway's own hosted checkout page</b> — redirect your customer there
          directly. No Optivize form or interstitial page: Razorpay serves its native payment page (UPI/cards/netbanking) and
          PayGlocal serves its secure international checkout. After payment the customer is returned to
          {" "}<code className="text-cyan-300 font-mono">status_page_url</code> (confirmation + automatic invoice download), and your
          webhook receives the signed <code className="text-cyan-300 font-mono">payment.success</code> event. The response also includes
          {" "}<code className="text-cyan-300 font-mono">checkout_mode</code> (<code className="text-slate-300">gateway_direct</code> or
          {" "}<code className="text-slate-300">hosted_page</code> if the gateway session could not be created and we fell back) and
          {" "}<code className="text-cyan-300 font-mono">hosted_page_url</code> as a fallback link. If any required customer detail is
          missing the API returns <code className="text-amber-300">400</code>. Omit it (default <code className="text-slate-300">false</code>)
          to get the Optivize-hosted pre-filled checkout form instead.
        </div>
      </div>
      <div className="rounded-xl border border-blue-800/60 bg-blue-500/5 p-4 my-3 text-sm">
        <div className="text-white font-semibold mb-1 flex items-center gap-2"><ArrowLeftRight size={14} className="text-blue-400"/> Return redirect (callback URL to your site)</div>
        <div className="text-slate-400 text-xs leading-relaxed">
          Pass <code className="text-blue-300 font-mono">"redirect_url": "https://yoursite.com/payment/return"</code> (or set a default per API key)
          and after the payment completes the customer is automatically sent back to your website from the Optivize confirmation page
          (~6&nbsp;second countdown + a "Return to merchant" button). Your URL receives signed query params:
        </div>
        <CodeBlock title="Return redirect — query params" lang="text" code={`payment_id       Optivize payment id
token            payment token
status           success | failed | declined | cancelled
transaction_id   gateway transaction id
reference_id     your reference_id (if provided)
invoice_number   Optivize invoice number (on success)
signature        HMAC-SHA256 hex of "payment_id|status|transaction_id|reference_id"
                 signed with your key's WEBHOOK SECRET — always verify it!`}/>
        <CodeBlock title="Verify the return signature — Python" lang="python" code={`import hmac, hashlib

def verify_return(params: dict, webhook_secret: str) -> bool:
    msg = "|".join([params["payment_id"], params["status"],
                    params.get("transaction_id", ""), params.get("reference_id", "")])
    expected = hmac.new(webhook_secret.encode(), msg.encode(), hashlib.sha256).hexdigest()
    return hmac.compare_digest(expected, params["signature"])`}/>
        <div className="text-slate-400 text-xs leading-relaxed mt-1">
          You can also fetch live status anytime: <code className="text-emerald-400 font-mono">GET /v1/payments/&#123;payment_id&#125;</code> for a
          single payment or <code className="text-emerald-400 font-mono">GET /v1/payments?payment_status=success&amp;limit=50</code> to list all
          your transactions with full customer details, gateway transaction ids and invoice numbers.
        </div>
      </div>
      <CodeBlock title="Response · 201" lang="json" code={`{
  "payment_id": "9d1c2f6e-...",
  "token": "kX3f...",
  "payment_url": "https://rzp.io/rzp/AbCdEf12",
  "checkout_mode": "gateway_direct",
  "hosted_page_url": "https://optivize.digital/pl/kX3f...",
  "status_page_url": "https://optivize.digital/pl/kX3f.../status",
  "gateway": "razorpay",
  "amount": 4999.0,
  "currency": "INR",
  "product_name": "Premium Website Package",
  "status": "active",
  "payment_status": "pending",
  "customer_details": {
    "first_name": "Rahul",
    "last_name": "Sharma",
    "email": "customer@example.com",
    "phone": "+919876543210",
    "address": "221B MG Road",
    "city": "Bengaluru",
    "state": "Karnataka",
    "pin_code": "560001",
    "country": "IN"
  },
  "auto_checkout": true,
  "expires_at": "2026-07-18T11:00:00+00:00",
  "created_at": "2026-07-17T11:00:00+00:00"
}`}/>

      <H2 icon={ListChecks} id="status">Retrieve & list payments</H2>
      <P><code className="text-emerald-400 font-mono">GET /v1/payments/{"{payment_id}"}</code> — fetch by <code>payment_id</code>, <code>token</code> or your own <code>reference_id</code>.</P>
      <P><code className="text-emerald-400 font-mono">GET /v1/payments?payment_status=success&amp;limit=50</code> — list your payments (newest first, max 200).</P>
      <CodeBlock title="Get payment status" code={`curl ${BASE}/v1/payments/ORDER-12345 \\
  -H "X-Api-Key: ok_live_YOUR_KEY" \\
  -H "X-Api-Secret: sk_live_YOUR_SECRET"`}/>
      <div className="rounded-xl border border-slate-700 p-4 my-3 text-sm">
        <div className="text-white font-semibold mb-2">Payment lifecycle</div>
        <div className="text-slate-400 text-xs space-y-1">
          <div><code className="text-amber-300">payment_status</code>: <code>pending</code> → <code className="text-emerald-400">success</code> | <code className="text-rose-400">failed</code> | <code className="text-rose-400">declined</code> | <code className="text-slate-300">cancelled</code></div>
          <div><code className="text-amber-300">status</code> (link): <code>active</code> → <code className="text-emerald-400">paid</code> | <code>expired</code> (24h TTL) | <code>cancelled</code></div>
          <div>On success, <code>transaction_id</code>, <code>invoice_number</code> and <code>paid_at</code> are populated.</div>
        </div>
      </div>

      <H2 icon={Webhook} id="webhooks">Webhooks</H2>
      <P>When a payment reaches a final state, Optivize POSTs a JSON event to your webhook URL (set on the key, or per payment).
        Events: <code className="text-cyan-300">payment.succeeded</code>, <code className="text-cyan-300">payment.failed</code>, <code className="text-cyan-300">payment.cancelled</code>.
        Delivery is retried up to 3 times. Always verify the signature before trusting the payload.</P>
      <CodeBlock title="Webhook headers" code={`X-Optivize-Event: payment.succeeded\nX-Optivize-Signature: sha256=<hex HMAC-SHA256 of the raw body, keyed with your whsec_ webhook secret>\nUser-Agent: Optivize-Webhooks/1.0`}/>
      <CodeBlock title="Webhook payload" lang="json" code={`{
  "event": "payment.succeeded",
  "created_at": "2026-07-17T11:05:00+00:00",
  "reference_id": "ORDER-12345",
  "data": {
    "payment_id": "9d1c2f6e-...",
    "payment_status": "success",
    "amount": 4999.0,
    "currency": "INR",
    "transaction_id": "pay_XXXXXXXX",
    "invoice_number": "INV-9D1C2F6E",
    "paid_at": "2026-07-17T11:05:00+00:00",
    "metadata": {"campaign": "summer"}
  }
}`}/>
      <CodeBlock title="Verify signature — Python" lang="python" code={`import hmac, hashlib

def verify(raw_body: bytes, signature_header: str, webhook_secret: str) -> bool:
    expected = hmac.new(webhook_secret.encode(), raw_body, hashlib.sha256).hexdigest()
    return hmac.compare_digest("sha256=" + expected, signature_header)`}/>
      <CodeBlock title="Verify signature — Node.js" lang="javascript" code={`const crypto = require("crypto");

function verify(rawBody, signatureHeader, webhookSecret) {
  const expected = "sha256=" + crypto
    .createHmac("sha256", webhookSecret)
    .update(rawBody)
    .digest("hex");
  return crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(signatureHeader));
}`}/>

      <H2 icon={AlertTriangle} id="errors">Errors & limits</H2>
      <FieldsTable rows={[
        ["401", "error", "—", "Missing or invalid X-Api-Key / X-Api-Secret"],
        ["403", "error", "—", "Key disabled, or gateway not allowed for this key"],
        ["404", "error", "—", "Payment not found (or belongs to another key)"],
        ["400", "error", "—", "Validation error — details in the response body"],
        ["429", "error", "—", "Rate limit exceeded — 60 requests/minute per key"],
      ]}/>
      <P>All errors return <code className="text-cyan-300 font-mono">{`{"detail": "..."}`}</code>. Amounts are in major currency units (rupees, not paise).
        Need an API key or help integrating? Contact <a href="mailto:sales@optivize.digital" className="text-cyan-300 underline">sales@optivize.digital</a>.</P>
    </div>
  );
}
